import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
} from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function CostCenter({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Description",
    "Status",
    "Actions"
  ]
  
  var accessors = [
    "id",
    "description",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Form state
  const [form, setForm] = useState({
    description: "",
    isActive: true, // Default to true
  });

  const resetLocalVariables = () => {
    setForm({
      description: "",
      isActive: true,
    });
    setSelectedData(null);
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/CostCenter";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    
    if (!value) {
      // If input is empty, reset to original list
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      return match1 || match2;
    });
    
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prepare data for API
    const apiData = {
      description: form.description || "",
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Cost Center", 
        actionTaken: "insert", 
        field: "Add", 
        newValue: `${apiData.description}`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("Cost Center created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create cost center";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    const apiData = {
      description: form.description || "",
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      let auditInfo = [];

      if(apiData.description !== selectedData.description){
        auditInfo.push({
          setupArea: "Cost Center",
          actionTaken: "update",
          field: "Description",
          newValue: `${apiData.description}`,
          oldValue: `${selectedData.description}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.isActive !== selectedData.isActive){
        auditInfo.push({
          setupArea: "Cost Center",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({data: auditInfo});
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Cost Center updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update cost center";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    setForm({
      description: data.description || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Cost Center"}</span>
              </div>
              <h3>Cost Center</h3>
            </div>
            <div className='p-4 space-y-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Cost Center"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"Cost Centers"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Cost Center"}
        mode="add"
      />
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Cost Center"}
        mode="edit"
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add", // "add", "edit"
}) {
  const [errorState, setErrorState] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.description) {
      setErrorState(true);
      return;
    }
    
    handleSubmit(e);
    setErrorState(false);
  };

  const handleClose = () => {
    onClose();
    setErrorState(false);
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] xl:w-[40%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">

            {/* Cost Center Form */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Cost Center Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Description:"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter cost center description"
                  isRequired={true}
                  error={errorState && !form.description}
                  errorMessage="Please fill up this field"
                />
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isActive !== undefined ? form.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
            <button
              onClick={handleClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default CostCenter